// Inventory Dashboard — stock health/warehouse overview, wired to the
// Inventory-specific backend aggregation queries via ONE combined GraphQL
// round trip (getInventoryDashboardDataAction), following the same pattern as
// DashboardScreen.tsx / SalesDashboardScreen.tsx.
//
// Dropped vs. the original brief (no backing data in the current schema):
// Stock Added/Issued Today, Inventory Aging, Stock In vs Stock Out (all need a
// per-transaction stock-movement ledger — stocktrack is running counters
// only), Inventory Value Trend (no historical snapshots), Warehouse
// Utilization (no warehouse/location concept), Near-Expiry alert (no expiry
// field). These are flagged as follow-up work needing schema changes.
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { ColumnDef } from "@tanstack/react-table";
import {
  AlertTriangle,
  Package,
  PackageX,
  Boxes,
  ShoppingBag,
  Archive,
  Tag,
} from "lucide-react";
import AppConstant from "../../../Globals/AppConstants";
import { getInventoryDashboardDataAction } from "../../../Store/actions/apparelAction";
import ApparelMenu from "../../SubComponents/apparelMenu";
import { StatCard } from "../../../shared/stat-card";
import { DataTable } from "../../../shared/data-table";
import { Skeleton } from "ui/skeleton";
import { Badge } from "ui/badge";
import { ChartCard } from "./ChartCard";
import { CategoryBarChart } from "./CategoryBarChart";

const inr = (v: any) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(v) || 0);
const num = (v: any) => new Intl.NumberFormat("en-IN").format(Number(v) || 0);

const TABLE_TABS = [
  { key: "lowStock", label: "Low Stock" },
  { key: "outOfStock", label: "Out of Stock" },
  { key: "recentProducts", label: "Recently Added" },
  { key: "pendingOrders", label: "Pending Purchase Orders" },
] as const;

type TableTabKey = (typeof TABLE_TABS)[number]["key"];

const PAGE_SIZE = 10;

const InventoryDashboardScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
  const { getInventoryDashboardData, getInventoryDashboardDataLoad } = apparelReducerState;

  const [activeTab, setActiveTab] = React.useState<TableTabKey>("lowStock");
  const [page, setPage] = React.useState(1);

  React.useEffect(() => { setPage(1); }, [activeTab]);

  React.useEffect(() => {
    dispatch(getInventoryDashboardDataAction({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const summary = getInventoryDashboardData?.getInventorySummary;
  const loading = getInventoryDashboardDataLoad && !summary;

  const categoryStock = (getInventoryDashboardData?.getCategoryStockDistribution || []).map((r: any) => ({ label: r.category, value: Number(r.stockQuantity) || 0 }));
  const topMoving = (getInventoryDashboardData?.getTopMovingProducts || []).map((r: any) => ({ label: r.productName, value: Number(r.quantitySold) || 0 }));
  const slowMoving = (getInventoryDashboardData?.getSlowMovingProducts || []).map((r: any) => ({ label: r.productName, value: Number(r.quantitySold) || 0 }));

  const lowStock = getInventoryDashboardData?.getLowStockItems || [];
  const outOfStock = (getInventoryDashboardData?.getLowStockItems || []).filter((i: any) => Number(i?.availableCount) <= 0);
  const negativeStock = getInventoryDashboardData?.getNegativeStockItems || [];
  const recentProducts = getInventoryDashboardData?.getRecentProducts || [];
  const pendingOrders = getInventoryDashboardData?.getPendingPurchaseOrders || [];

  const recentProductsCsvRows = React.useMemo(
    () => recentProducts.map((r: any) => ({ ...r, mrp: Math.round(Number(r.mrp) || 0), createdOn_fmt: r.createdOn ? moment(r.createdOn).format("DD-MMM-YYYY") : "-" })),
    [recentProducts]
  );
  const pendingOrdersCsvRows = React.useMemo(
    () => pendingOrders.map((r: any) => ({ ...r, status_label: r.status === 1 ? "New" : "In Progress", createdOn_fmt: r.createdOn ? moment(r.createdOn).format("DD-MMM-YYYY") : "-" })),
    [pendingOrders]
  );

  const lowStockColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "productName", header: "Product", cell: ({ row }) => row.original?.productName },
      { id: "productId", header: AppConstant?.productId, cell: ({ row }) => row.original?.productId ? (
        <button type="button" onClick={() => navigate("/inventoryManagement", { state: { auditRecordId: row.original.productDetailId, auditSearchCode: row.original.productId } })} className="text-primary hover:underline">
          {row.original.productId}
        </button>
      ) : "-" },
      { id: "category", header: "Category", cell: ({ row }) => row.original?.category ?? "-" },
      {
        id: "availableCount",
        header: "Available",
        cell: ({ row }) => {
          const available = Number(row.original?.availableCount);
          return <span className={available <= 0 ? "font-medium text-destructive" : "font-medium text-warning"}>{num(available)}</span>;
        },
      },
      { id: "minimumQuantity", header: "Reorder Level", cell: ({ row }) => num(row.original?.minimumQuantity) },
    ],
    [navigate]
  );

  const recentProductsColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "productName", header: "Product", cell: ({ row }) => row.original?.productName },
      { id: "productId", header: AppConstant?.productId, cell: ({ row }) => row.original?.productId ? (
        <button type="button" onClick={() => navigate("/inventoryManagement", { state: { auditRecordId: row.original.productDetailId, auditSearchCode: row.original.productId } })} className="text-primary hover:underline">
          {row.original.productId}
        </button>
      ) : "-" },
      { id: "category", header: "Category", cell: ({ row }) => row.original?.category ?? "-" },
      { id: "mrp", header: "MRP", cell: ({ row }) => <span className="tabular-nums">{inr(row.original?.mrp)}</span> },
      { id: "createdOn", header: "Added On", cell: ({ row }) => (row.original?.createdOn ? moment(row.original.createdOn).format("DD-MMM-YYYY") : "-") },
    ],
    [navigate]
  );

  const pendingOrdersColumns = React.useMemo<ColumnDef<any, any>[]>(
    () => [
      { id: "description", header: "Description", cell: ({ row }) => row.original?.description ? (
        <button type="button" onClick={() => navigate("/procurement")} className="text-primary hover:underline text-left">
          {row.original.description}
        </button>
      ) : "-" },
      { id: "category", header: "Category", cell: ({ row }) => row.original?.category ?? "-" },
      { id: "subCategory", header: "Sub Category", cell: ({ row }) => row.original?.subCategory ?? "-" },
      { id: "count", header: "Qty", cell: ({ row }) => num(row.original?.count) },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => <Badge variant="warning">{row.original?.status == 1 ? "New" : "In Progress"}</Badge>,
      },
      { id: "createdOn", header: "Raised On", cell: ({ row }) => (row.original?.createdOn ? moment(row.original.createdOn).format("DD-MMM-YYYY") : "-") },
    ],
    [navigate]
  );

  const allTabData: Record<TableTabKey, { columns: ColumnDef<any, any>[]; rows: any[] }> = {
    lowStock: { columns: lowStockColumns, rows: lowStock },
    outOfStock: { columns: lowStockColumns, rows: outOfStock },
    recentProducts: { columns: recentProductsColumns, rows: recentProducts },
    pendingOrders: { columns: pendingOrdersColumns, rows: pendingOrders },
  };

  const activeTabData = allTabData[activeTab];
  const pagedData = activeTabData.rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="mz h-full overflow-y-auto px-6 py-4">
      <ApparelMenu selectedKey="4" />

      <div className="mb-4 flex items-center gap-1 border-b">
        <button type="button" className="border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground">
          {AppConstant.menus.inventoryDashboard}
        </button>
      </div>

      {/* Alerts */}
      {!loading && (Number(summary?.lowStockCount) > 0 || Number(summary?.overstockCount) > 0 || negativeStock.length > 0) && (
        <div className="mb-4 flex flex-wrap gap-2">
          {Number(summary?.lowStockCount) > 0 && (
            <Badge variant="destructive">{num(summary?.lowStockCount)} products below reorder level</Badge>
          )}
          {Number(summary?.overstockCount) > 0 && (
            <Badge variant="warning">{num(summary?.overstockCount)} overstocked products</Badge>
          )}
          {negativeStock.length > 0 && (
            <Badge variant="accent">{num(negativeStock.length)} products with negative stock</Badge>
          )}
        </div>
      )}

      {/* KPI cards */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {loading ? (
          Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)
        ) : (
          <>
            <StatCard label="Total Products" value={num(summary?.totalProducts)} icon={Package} />
            <StatCard label="Total Stock Quantity" value={num(summary?.totalStockQuantity)} icon={Boxes} />
            <StatCard label="Inventory Value" value={inr(summary?.inventoryValue)} icon={ShoppingBag} />
            <StatCard
              label="Low Stock"
              value={num(summary?.lowStockCount)}
              trend={Number(summary?.lowStockCount) > 0 ? "down" : "neutral"}
              icon={AlertTriangle}
            />
            <StatCard label="Out of Stock" value={num(summary?.outOfStockCount)} icon={PackageX} />
            <StatCard label="Overstock" value={num(summary?.overstockCount)} icon={Package} />
            <StatCard label="Dead Stock" value={num(summary?.deadStockCount)} icon={Archive} />
            <StatCard label="Categories" value={num(summary?.categoriesCount)} icon={Tag} />
          </>
        )}
      </div>

      {/* Charts */}
      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Stock by Category" loading={loading} isEmpty={!categoryStock.length}>
          <CategoryBarChart data={categoryStock} valueFormatter={num} />
        </ChartCard>
        <ChartCard title="Top Moving Products" loading={getInventoryDashboardDataLoad && !getInventoryDashboardData} isEmpty={!topMoving.length}>
          <CategoryBarChart data={topMoving} valueFormatter={num} layout="vertical" />
        </ChartCard>
        <ChartCard title="Slow Moving Products" loading={getInventoryDashboardDataLoad && !getInventoryDashboardData} isEmpty={!slowMoving.length}>
          <CategoryBarChart data={slowMoving} valueFormatter={num} layout="vertical" />
        </ChartCard>
      </div>

      {/* Tables */}
      <div className="mb-2 flex flex-wrap gap-1 border-b">
        {TABLE_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            className={
              activeTab === tab.key
                ? "border-b-2 border-primary px-3 py-2 text-sm font-medium text-foreground"
                : "border-b-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            }
          >
            {tab.label}
          </button>
        ))}
      </div>
      <DataTable
        columns={activeTabData.columns}
        data={pagedData}
        loading={getInventoryDashboardDataLoad && !getInventoryDashboardData}
        csvExport={
          activeTab === "lowStock" || activeTab === "outOfStock"
            ? { fileName: `${activeTab === "lowStock" ? "low-stock" : "out-of-stock"}_${moment().format("YYYY-MM-DD")}`, rows: activeTabData.rows, columns: [{ key: "productName", header: "Product" }, { key: "productId", header: AppConstant?.productId ?? "Product ID" }, { key: "category", header: "Category" }, { key: "availableCount", header: "Available" }, { key: "minimumQuantity", header: "Reorder Level" }] }
            : activeTab === "recentProducts"
            ? { fileName: `recently-added-products_${moment().format("YYYY-MM-DD")}`, rows: recentProductsCsvRows, columns: [{ key: "productName", header: "Product" }, { key: "productId", header: AppConstant?.productId ?? "Product ID" }, { key: "category", header: "Category" }, { key: "mrp", header: "MRP" }, { key: "createdOn_fmt", header: "Added On" }] }
            : { fileName: `pending-purchase-orders_${moment().format("YYYY-MM-DD")}`, rows: pendingOrdersCsvRows, columns: [{ key: "description", header: "Description" }, { key: "category", header: "Category" }, { key: "subCategory", header: "Sub Category" }, { key: "count", header: "Qty" }, { key: "status_label", header: "Status" }, { key: "createdOn_fmt", header: "Raised On" }] }
        }
        pagination={{
          page,
          total: activeTabData.rows.length,
          pageSize: PAGE_SIZE,
          onChange: setPage,
        }}
      />
    </div>
  );
};

export default InventoryDashboardScreen;
